import {
    ReactElement,
    useMemo,
    useRef,
    FormEventHandler,
    forwardRef,
    useImperativeHandle,
    useState
} from 'react';
import { GeneralTeamSettingsSectionFormRef } from './general-team-settings-section';
import { Team, TeamNotifications, TeamNotification } from '@/api';
import { useUpdateTeamAction } from '../actions';
import {
    UISection,
    UISectionContent,
    UISectionFooter,
    UISectionHeader,
    UISectionTableHeader,
    UISwitch,
    UIText,
    UITooltip
} from '@/ui';
import { useCompanyContext } from '../providers';
import { isEqual } from 'lodash';
import { useCompaniesTranslation } from '../hooks';

type TeamNotificationsSettingsSectionProps = { team: Team };

export function TeamNotificationsSettingsSection(
    props: TeamNotificationsSettingsSectionProps
): ReactElement {
    const { team } = props;

    const { company } = useCompanyContext();

    const formRef = useRef<GeneralTeamSettingsSectionFormRef>(null);
    const updateTeam = useUpdateTeamAction({ teamId: team.id });

    const hasSMSSubscription = useMemo(() => {
        if (!company.smsSubscription) {
            return false;
        }

        return ['active', 'trialing', 'past_due'].includes(
            company.smsSubscription.status
        );
    }, [company]);

    function onSubmit(data: { notifications: TeamNotifications }) {
        const { notifications } = data;
        updateTeam
            .updateTeam({
                notifications
            })
            .catch(() => {}); // Error handled in the action
    }

    return (
        <TeamNotificationsSettingsSectionForm
            canEdit={updateTeam.isAllowed}
            hasSMSSubscription={hasSMSSubscription}
            ref={formRef}
            initialNotifications={team.notifications}
            loading={updateTeam.loading}
            onSubmit={onSubmit}
        />
    );
}

type TeamNotificationsSettingsSectionFormProps = {
    canEdit: boolean;
    hasSMSSubscription: boolean;
    initialNotifications: TeamNotifications;
    loading: boolean;
    onSubmit: (data: { notifications: TeamNotifications }) => void;
};

type TeamNotificationsSettingsSectionFormRef = {
    resetForm: () => void;
};

// The notifications rendered, in display order. `teamBroadcast` also exists on
// TeamNotifications but has never been exposed here.
const NOTIFICATION_KEYS: (keyof Omit<TeamNotifications, 'teamBroadcast'>)[] = [
    'taskStarted',
    'taskExtended',
    'taskCompleted',
    'taskNote',
    'alertTriggered',
    'alertAssigned',
    'alertUnassigned',
    'alertResolved'
];

const TeamNotificationsSettingsSectionForm = forwardRef<
    TeamNotificationsSettingsSectionFormRef,
    TeamNotificationsSettingsSectionFormProps
>((props, ref): ReactElement => {
    const {
        canEdit,
        hasSMSSubscription,
        initialNotifications,
        loading,
        onSubmit
    } = props;

    const { t } = useCompaniesTranslation();

    const [notifications, setNotifications] = useState(initialNotifications);

    const haveValuesChanged = useMemo(() => {
        return !isEqual(notifications, initialNotifications);
    }, [notifications, initialNotifications]);

    function submitForm() {
        onSubmit?.({
            notifications
        });
    }

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();
        submitForm();
    };

    function resetForm() {
        setNotifications(initialNotifications);
    }

    useImperativeHandle(ref, () => ({
        resetForm() {
            resetForm();
        }
    }));

    return (
        <UISection>
            <form onSubmit={handleSubmit} noValidate>
                <UISectionHeader
                    title={t(
                        'components.team_notifications_settings_section.title'
                    )}
                    subtitle={t(
                        'components.team_notifications_settings_section.subtitle'
                    )}
                />
                <UISectionTableHeader
                    items={[
                        { text: t('shared.notifications.type_column') },
                        {
                            items: [
                                {
                                    text: t('shared.notifications.app_column'),
                                    tooltip: t(
                                        'shared.notifications.app_column_tooltip'
                                    )
                                },
                                {
                                    text: t(
                                        'shared.notifications.email_column'
                                    ),
                                    tooltip: t(
                                        'shared.notifications.email_column_tooltip'
                                    )
                                },
                                {
                                    text: t('shared.notifications.sms_column'),
                                    tooltip: t(
                                        'shared.notifications.sms_column_tooltip'
                                    )
                                }
                            ]
                        }
                    ]}
                />
                <UISectionContent>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 24
                        }}
                    >
                        {NOTIFICATION_KEYS.map((key) => (
                            <RowItem
                                key={key}
                                title={t(`shared.notifications.${key}_label`)}
                                description={t(
                                    `shared.notifications.${key}_description`
                                )}
                                notification={notifications[key]}
                                onChange={(notification) => {
                                    setNotifications({
                                        ...notifications,
                                        [key]: notification
                                    });
                                }}
                                canEdit={canEdit}
                                hasSMSSubscription={hasSMSSubscription}
                            />
                        ))}
                    </div>
                </UISectionContent>
                {haveValuesChanged && (
                    <UISectionFooter
                        actions={[
                            {
                                variant: 'primary',
                                type: 'submit',
                                loading: loading,
                                disabled: loading,
                                text: loading
                                    ? t('shared.labels.saving')
                                    : t('shared.labels.save')
                            },
                            {
                                text: t('shared.labels.cancel'),
                                disabled: loading,
                                onClick: resetForm
                            }
                        ]}
                    />
                )}
            </form>
        </UISection>
    );
});

type RowItemProps = {
    title: string;
    description: string;
    notification: TeamNotification;
    onChange: (notification: TeamNotification) => void;
    canEdit: boolean;
    hasSMSSubscription: boolean;
};
function RowItem(props: RowItemProps): ReactElement {
    const {
        title,
        description,
        notification,
        onChange,
        canEdit,
        hasSMSSubscription
    } = props;

    const { t } = useCompaniesTranslation();

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 16
            }}
        >
            <div
                style={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    gap: 4
                }}
            >
                <UIText>{title}</UIText>
                <UIText type="default2">{description}</UIText>
            </div>

            <div
                style={{
                    display: 'flex',
                    flex: 1
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flex: 1
                    }}
                >
                    <UISwitch
                        checked={notification.app}
                        type="default2"
                        onChange={() => {
                            onChange({
                                ...notification,
                                app: !notification.app
                            });
                        }}
                        disabled={!canEdit}
                        small
                    />
                </div>

                <div
                    style={{
                        display: 'flex',
                        flex: 1
                    }}
                >
                    <UISwitch
                        checked={notification.email}
                        type="default2"
                        onChange={() => {
                            onChange({
                                ...notification,
                                email: !notification.email
                            });
                        }}
                        disabled={!canEdit}
                        small
                    />
                </div>

                <div
                    style={{
                        display: 'flex',
                        flex: 1
                    }}
                >
                    {canEdit && !hasSMSSubscription ? (
                        <UITooltip
                            text={t(
                                'shared.notifications.sms_billing_required'
                            )}
                        >
                            <UISwitch
                                checked={notification.sms}
                                type="default2"
                                onChange={() => {
                                    onChange({
                                        ...notification,
                                        sms: !notification.sms
                                    });
                                }}
                                disabled={!canEdit || !hasSMSSubscription}
                                small
                            />
                        </UITooltip>
                    ) : (
                        <UISwitch
                            checked={notification.sms}
                            type="default2"
                            onChange={() => {
                                onChange({
                                    ...notification,
                                    sms: !notification.sms
                                });
                            }}
                            disabled={!canEdit || !hasSMSSubscription}
                            small
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
